import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { 
  Building2, 
  Plus, 
  Clock,
  CheckCircle2,
  XCircle,
  Briefcase,
  Users,
  Shield,
} from "lucide-react";
import type { Company, PersonToCompany } from "@shared/schema";

interface AffiliationWithCompany {
  id: string;
  personId: string;
  companyId: string;
  role: string | null;
  status: string;
  company: Company;
}

export default function UserAffiliations() {
  const { toast } = useToast();
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");

  const { data: affiliations = [], isLoading } = useQuery<AffiliationWithCompany[]>({
    queryKey: ["/api/affiliations/my"],
  });

  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: ["/api/companies"],
  });

  const requestMutation = useMutation({
    mutationFn: async (companyId: string) => {
      const response = await apiRequest("POST", "/api/affiliations/request", { companyId });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/affiliations/my"] });
      setRequestDialogOpen(false);
      setSelectedCompanyId("");
      toast({
        title: "Affiliation Requested",
        description: "Your request has been submitted for review",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Request Failed",
        description: error.message || "Failed to request affiliation",
        variant: "destructive",
      });
    },
  });

  const affiliatedCompanyIds = affiliations.map(a => a.companyId);
  const availableCompanies = companies.filter(c => !affiliatedCompanyIds.includes(c.id));

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle2 className="h-3 w-3 mr-1" />Approved</Badge>;
      case "pending":
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case "rejected":
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRoleBadge = (role: string | null) => {
    switch (role) {
      case "admin":
        return <Badge variant="default"><Shield className="h-3 w-3 mr-1" />Admin</Badge>;
      case "contributor":
        return <Badge variant="secondary"><Users className="h-3 w-3 mr-1" />Contributor</Badge>;
      case "member":
        return <Badge variant="outline"><Briefcase className="h-3 w-3 mr-1" />Member</Badge>;
      default:
        return <Badge variant="outline">{role || "Member"}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="text-affiliations-title">My Affiliations</h1>
          <p className="text-muted-foreground">
            Companies you are affiliated with
          </p>
        </div>
        <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-request-affiliation">
              <Plus className="h-4 w-4 mr-2" />
              Request Affiliation
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Company Affiliation</DialogTitle>
              <DialogDescription>
                Select a company you are affiliated with. Your request will be reviewed by an administrator.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                <SelectTrigger data-testid="select-company">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  {availableCompanies.length === 0 ? (
                    <SelectItem value="none" disabled>No companies available</SelectItem>
                  ) : (
                    availableCompanies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRequestDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => requestMutation.mutate(selectedCompanyId)}
                  disabled={!selectedCompanyId || requestMutation.isPending}
                  data-testid="button-submit-affiliation"
                >
                  Submit Request
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {affiliations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No Affiliations Yet</h3>
            <p className="text-muted-foreground mb-4">
              Request affiliation with a company to start creating content on their behalf
            </p>
            <Button onClick={() => setRequestDialogOpen(true)} data-testid="button-first-affiliation">
              <Plus className="h-4 w-4 mr-2" />
              Request Your First Affiliation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {affiliations.map((affiliation) => (
            <Card key={affiliation.id} data-testid={`card-affiliation-${affiliation.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{affiliation.company?.name || "Unknown Company"}</CardTitle>
                      <CardDescription className="text-xs">
                        {affiliation.company?.industry || "No industry specified"}
                      </CardDescription>
                    </div>
                  </div>
                  {getStatusBadge(affiliation.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Role:</span>
                    {getRoleBadge(affiliation.role)}
                  </div>
                  {affiliation.status === "approved" && affiliation.role === "admin" && (
                    <Badge variant="outline" className="text-xs">
                      Can manage team
                    </Badge>
                  )}
                </div>
                {affiliation.status === "pending" && (
                  <p className="text-xs text-muted-foreground mt-3">
                    Awaiting approval from company administrator
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
